/**
 * FolderGridView · AI 分类页文件夹宫格视图（react-virtualized 窗口化版）
 *
 * 使用 AutoSizer + Grid 替换全量渲染，只渲染可见区（+ overscan 行）。
 * 所有数据与回调通过 props 透传，不持有本地 state，不直接读 Redux。
 * 待归档虚拟项作为最后一个 cell，不挂 Dropdown / 不参与拖拽。
 *
 * 框选（BoxSelect）挂在本组件的 .gridViewport 上、滚动容器指向 Grid 内部滚动节点，
 * 几何坐标由 rectsFromGridLayout 按 index 推算（覆盖视口外未渲染的项）。
 */

import React, { useCallback, useEffect, useRef } from 'react'
import { AutoSizer, Grid, GridCellProps } from 'react-virtualized'
import { Dropdown } from 'antd'
import FolderCard from './FolderCard'
import BoxSelect from '../BoxSelect/BoxSelect'
import {
  rectsFromGridLayout,
  edgeScrollSpeed
} from '../BoxSelect/boxSelectGeometry'
import {
  CARD_WIDTH,
  CARD_HEIGHT,
  COLUMN_WIDTH,
  GRID_ROW_HEIGHT
} from './gridLayout'
import { useEdgeFadeMask } from './useEdgeFadeMask'
import styles from './FolderList.module.scss'
import type { FolderListItem } from '../../../../types/ai-classify'

interface FolderGridViewProps {
  /** 当前已排序的顶层文件夹列表（来自 sortedTopFolders） */
  folders: FolderListItem[]
  /** 「待归档」虚拟项，非空时追加在列表末尾；不参与多选 / 框选 / 拖拽 */
  unclassifiedFolder: FolderListItem | null
  /** 构造单个文件夹的右键菜单（批量 / 单条由 FolderList 内部判断） */
  buildFolderContextMenu: (folder: FolderListItem) => React.ReactElement
  /** 多选模式开关 */
  manageMode: boolean
  /** 当前已勾选的 folderId 列表 */
  selectedFolderIds: string[]
  /** 是否管理员（控制 draggable） */
  isAdmin: boolean
  /** 刚创建的 folderId 集合（进场动画，~2s 后清） */
  recentlyCreatedIds: readonly string[]
  /** 新建未进入的 folderId 集合（NEW 角标） */
  newBadgeFolderIds: readonly string[]
  /** 正在归档中的 folderId 集合 */
  archivingFolderIds: Set<string>
  /** 归档已超时的 folderId 集合 */
  timeoutFolderIds: Set<string>
  /** 当前拖动会话的源 folderId 列表；null 表示无拖动会话 */
  draggedIds: string[] | null
  /** 当前拖动悬停的目标 folderId */
  dragOverFolderId: string | null
  /** 卡片整块点击（跳详情 / 切换勾选） */
  onCardClick: (folderId: string) => void
  /** checkbox 单独点击（进多选 + toggle） */
  onSelectClick: (folderId: string) => void
  /** 拖拽开始 · 对齐 index.tsx handleCardDragStart 签名（HTMLElement） */
  onCardDragStart: (
    e: React.DragEvent<HTMLElement>,
    folder: FolderListItem
  ) => void
  /** 拖拽结束 */
  onCardDragEnd: (
    e: React.DragEvent<HTMLElement>,
    folder: FolderListItem
  ) => void
  /** 拖拽悬停（必须 preventDefault 才能触发 drop） */
  onCardDragOver: (
    e: React.DragEvent<HTMLElement>,
    folder: FolderListItem
  ) => void
  /** 拖拽离开目标 */
  onCardDragLeave: (
    e: React.DragEvent<HTMLElement>,
    folder: FolderListItem
  ) => void
  /** 释放拖拽 */
  onCardDrop: (e: React.DragEvent<HTMLElement>, folder: FolderListItem) => void
  /**
   * 框选起手容器 · BoxSelect 在此元素上绑 mousedown。
   * 用 FolderList 的 .container（工具栏 + 网格 + 一圈 padding 边距），而非铺满的
   * .gridViewport —— 后者第一张卡贴左上角无空白可起手，框选会"无法触发"。
   * 滚动容器仍是 Grid 内部节点（scrollElementRef），几何坐标仍按 rectsFromGridLayout 推算。
   */
  boxSelectContainerRef: React.RefObject<HTMLDivElement>
  /** 框选实时命中 · 上抛当前框中的 folderId 列表（含空集） */
  onBoxSelect: (ids: string[]) => void
  /** 框选开始（越过阈值）· 进入多选 + 标记会话进行中 */
  onBoxSelectStart: () => void
  /** 框选结束（松手 / Esc / 失焦）· 收尾多选生命周期 */
  onBoxSelectEnd: () => void
  /** 框选空白单击 · 起点在空白且未拖动时触发（点空白退出多选） */
  onBoxEmptyClick: () => void
}

/**
 * 文件夹宫格视图（窗口化）
 *
 * 用 react-virtualized AutoSizer + Grid 替换原 .grid map 全量渲染。
 * · AutoSizer 撑满 .gridViewport 并把宽高注入 Grid
 * · 列数 = Math.max(1, Math.floor(width / COLUMN_WIDTH))，保底 1 列
 * · 待归档虚拟项追加在 cells 末尾，末行多余 cell 返回 null 补位
 */
const FolderGridView: React.FC<FolderGridViewProps> = ({
  folders,
  unclassifiedFolder,
  buildFolderContextMenu,
  manageMode,
  selectedFolderIds,
  isAdmin,
  recentlyCreatedIds,
  newBadgeFolderIds,
  archivingFolderIds,
  timeoutFolderIds,
  draggedIds,
  dragOverFolderId,
  onCardClick,
  onSelectClick,
  onCardDragStart,
  onCardDragEnd,
  onCardDragOver,
  onCardDragLeave,
  onCardDrop,
  boxSelectContainerRef,
  onBoxSelect,
  onBoxSelectStart,
  onBoxSelectEnd,
  onBoxEmptyClick
}) => {
  // 待归档虚拟项追加到列表末尾，作为最后一个 cell
  const cells: FolderListItem[] = unclassifiedFolder
    ? [...folders, unclassifiedFolder]
    : folders
  const cellCount = cells.length

  // 框选挂载容器 · BoxSelect 在其内绑 mousedown 并查找 Grid 内部滚动节点
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Grid 内部滚动 DOM 节点 · mount 后从 wrapper 内抓取，作为框选的显式滚动容器
  // 显式 HTMLElement | null 选 useRef 可变重载 · effect 里要给 .current 赋值
  const scrollElRef = useRef<HTMLElement | null>(null)
  // 当前列数 · AutoSizer 渲染时写入，框选开始算几何坐标时读取
  const columnCountRef = useRef(1)
  // Grid 实例 ref · 用于选中态变化时清掉滚动缓存（见下方 effect）
  const gridRef = useRef<Grid>(null)
  // 滚动边缘渐隐遮罩 · 上/下边缘透明渐变，内容滚入滚出更平滑
  const { onScroll: onFadeScroll, maskStyle } = useEdgeFadeMask()

  // 边缘自动滚动 · 拖动文件夹到 Grid 视口边缘时自动滚动，跨视口拖放
  const dragScrollRafRef = useRef(0)
  const EDGE = 48
  const MAX_SPEED = 18

  const stopDragScroll = useCallback(() => {
    if (dragScrollRafRef.current) cancelAnimationFrame(dragScrollRafRef.current)
    dragScrollRafRef.current = 0
  }, [])

  const handleViewportDragOver = useCallback(
    (e: React.DragEvent) => {
      const scroller = scrollElRef.current
      if (!scroller) return
      const rect = scroller.getBoundingClientRect()
      const distTop = e.clientY - rect.top
      const distBottom = rect.bottom - e.clientY
      let dir = 0
      if (distTop < EDGE)
        dir = -edgeScrollSpeed(EDGE - distTop, EDGE, MAX_SPEED)
      else if (distBottom < EDGE)
        dir = edgeScrollSpeed(EDGE - distBottom, EDGE, MAX_SPEED)
      stopDragScroll()
      if (dir !== 0) {
        const tick = () => {
          const max = scroller.scrollHeight - scroller.clientHeight
          scroller.scrollTop = Math.max(
            0,
            Math.min(scroller.scrollTop + dir, max)
          )
          dragScrollRafRef.current = requestAnimationFrame(tick)
        }
        dragScrollRafRef.current = requestAnimationFrame(tick)
      }
    },
    [stopDragScroll]
  )

  // 组件卸载时停止残留的 rAF
  useEffect(() => stopDragScroll, [stopDragScroll])

  // Grid 挂载后抓它的内部滚动 DOM 节点（className ai-folder-virtualized-grid）
  // 依赖 [] 只在 mount 后执行一次：Grid 滚动节点挂载后稳定不变；
  // 视图整体切换会重挂载本组件，[] effect 仍会重跑，无需担心过期引用
  useEffect(() => {
    scrollElRef.current =
      wrapperRef.current?.querySelector<HTMLElement>(
        '.ai-folder-virtualized-grid'
      ) || null
  }, [])

  // 选中态变化时清掉 Grid 的滚动缓存，让可见单元格立即反映新选中态。
  // react-virtualized 滚动中（isScrolling）会缓存 cell 渲染结果（含旧 selected），
  // 默认要等滚动停止才清缓存重渲染 —— 这会让框选拖到边缘自动滚动时，新覆盖到的
  // 文件夹要等滚动停才高亮。recomputeGridSize() 会清 _cellCache + forceUpdate。
  // 只在 selectedFolderIds 变化时触发：普通滚动（无选中变化）不调用，性能不受影响。
  useEffect(() => {
    if (gridRef.current) gridRef.current.recomputeGridSize()
  }, [selectedFolderIds])

  // 框选全量项坐标 · 只含真实 folder（不含待归档虚拟项，虚拟项不可框选）
  const getItemRects = useCallback(
    () =>
      rectsFromGridLayout(
        folders.map((f) => f.folderId),
        columnCountRef.current,
        COLUMN_WIDTH,
        GRID_ROW_HEIGHT,
        CARD_WIDTH,
        CARD_HEIGHT
      ),
    [folders]
  )

  /**
   * Grid cell 渲染函数
   *
   * @param columnCount - 当前窗口宽度对应的列数，从 AutoSizer 闭包传入
   * @param cellProps   - react-virtualized GridCellProps（含 columnIndex / rowIndex / style / key）
   * @returns React 节点；末行补空 cell 时返回 null
   */
  const renderCell = (
    columnCount: number,
    cellProps: GridCellProps
  ): React.ReactNode => {
    const { columnIndex, rowIndex, style, key } = cellProps
    const index = columnCount * rowIndex + columnIndex

    // 末行补空：实际 items 不足一整行时返回 null 填位
    if (index >= cellCount) return null

    const folder = cells[index]

    // 待归档虚拟项：不挂 Dropdown、不传选中 / 拖拽 props
    // （银灰 + 禁选由 FolderCard 内部根据 folder.virtual 自处理）
    if (folder.virtual) {
      return (
        <div key={key} style={style}>
          <FolderCard folder={folder} onClick={onCardClick} />
        </div>
      )
    }

    return (
      <div key={key} style={style}>
        <Dropdown
          popupRender={() => buildFolderContextMenu(folder)}
          trigger={['contextMenu']}
          // 多选模式下：仅对"已被勾选"的卡放行（弹批量菜单）；
          // 未被勾选的卡禁用菜单，避免误触单条动作
          disabled={
            manageMode && selectedFolderIds.indexOf(folder.folderId) < 0
          }
        >
          <FolderCard
            folder={folder}
            onClick={onCardClick}
            onSelectClick={onSelectClick}
            justCreated={recentlyCreatedIds.indexOf(folder.folderId) >= 0}
            showNewBadge={newBadgeFolderIds.indexOf(folder.folderId) >= 0}
            archiving={archivingFolderIds.has(folder.folderId)}
            archivingTimedOut={timeoutFolderIds.has(folder.folderId)}
            selectMode={manageMode}
            selected={
              manageMode && selectedFolderIds.indexOf(folder.folderId) >= 0
            }
            draggable={isAdmin}
            onDragStart={onCardDragStart}
            onDragEnd={onCardDragEnd}
            onDragOver={onCardDragOver}
            onDragLeave={onCardDragLeave}
            onDrop={onCardDrop}
            dragging={!!draggedIds && draggedIds.indexOf(folder.folderId) >= 0}
            dragOver={dragOverFolderId === folder.folderId}
          />
        </Dropdown>
      </div>
    )
  }

  return (
    // gridViewport · flex:1 撑满 .viewport 剩余高度，min-height:0 避免 flex 子元素撑出容器；
    // AutoSizer 据此测量可用宽高后注入 Grid，Grid 自己内部滚动（不需要再套 overflow:auto）
    <div
      className={styles.gridViewport}
      ref={wrapperRef}
      onDragOver={handleViewportDragOver}
      onDrop={stopDragScroll}
      onDragLeave={stopDragScroll}
      onDragEnd={stopDragScroll}
    >
      <AutoSizer>
        {({ width, height }) => {
          // 保底 1 列，避免 width 极小时出现 NaN / 0 列
          const columnCount = Math.max(1, Math.floor(width / COLUMN_WIDTH))
          const rowCount = Math.ceil(cellCount / columnCount)
          // 同步当前列数给框选几何模型（render 期写入，框选开始时读取）
          columnCountRef.current = columnCount

          return (
            <Grid
              ref={gridRef}
              className="ai-folder-virtualized-grid"
              width={width}
              height={height}
              columnWidth={COLUMN_WIDTH}
              rowHeight={GRID_ROW_HEIGHT}
              columnCount={columnCount}
              rowCount={rowCount}
              overscanRowCount={3}
              cellRenderer={(cellProps) => renderCell(columnCount, cellProps)}
              onScroll={onFadeScroll}
              style={{ outline: 'none', ...maskStyle }}
            />
          )
        }}
      </AutoSizer>
      {/* 鼠标框选 · 绑在 .gridViewport 上，滚动容器指向 Grid 内部滚动节点，
          几何坐标由 getItemRects（rectsFromGridLayout）按 index 推算，覆盖视口外项 */}
      <BoxSelect
        containerRef={boxSelectContainerRef}
        itemAttr="data-folder-id"
        scrollElementRef={scrollElRef}
        getItemRects={getItemRects}
        onSelect={onBoxSelect}
        onSelectStart={onBoxSelectStart}
        onSelectEnd={onBoxSelectEnd}
        onEmptyClick={onBoxEmptyClick}
      />
    </div>
  )
}

export default FolderGridView
